import { Component, computed, DestroyRef, inject, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { TagModule } from 'primeng/tag';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { FinanceService } from '../../core/services/finance.service';
import { StoreService } from '../../core/services/store.service';
import { CurrencyService } from '../../core/services/currency.service';
import { StoreLiteDto } from '../../core/models/store.models';
import { storePicker } from '../../core/utils/store-picker';
import {
  FinanceOverviewDto,
  DebtAgingDto,
  DebtAgingBucket,
  RecurringExpenseDto,
  ExpenseCategory,
  PaymentMethod,
} from '../../core/models/finance.models';
import { MoneyFixedPipe } from '../../shared/pipes/money.pipe';

@Component({
  selector: 'app-finance',
  standalone: true,
  imports: [
    MoneyFixedPipe,
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    SelectModule,
    InputNumberModule,
    InputTextModule,
    DatePickerModule,
    TagModule,
    ConfirmDialogModule,
    TranslatePipe,
    RouterLink,
  ],
  providers: [ConfirmationService],
  templateUrl: './finance.component.html',
})
export class FinanceComponent implements OnInit {
  private svc = inject(FinanceService);
  private storeSvc = inject(StoreService);
  readonly currencySvc = inject(CurrencyService);
  private confirm = inject(ConfirmationService);
  private translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  overview = signal<FinanceOverviewDto | null>(null);
  recurring = signal<RecurringExpenseDto[]>([]);
  loading = signal(false);

  /** Skeleton kartalar soni — P&L va budjet qatorlari ham to'rttadan. */
  readonly overviewSkeletons = [1, 2, 3, 4];

  stores = signal<StoreLiteDto[]>([]);
  filterStoreId: string | null = null;

  /** Bitta do'konli biznesda tanlagichlar yashiriladi — `storePicker` ga qarang. */
  readonly storePick = storePicker(this.stores);
  range: Date[] = [];

  /**
   * Qaysi valyuta daftari ko'rsatilyapti. Bo'sh — backend kotirovka valyutasini
   * oladi. Valyutalar ARALASHTIRILMAYDI (I1): boshqasini ko'rish uchun so'rov
   * qayta yuboriladi.
   */
  selectedCurrency = signal('');

  /** Ko'rsatilayotgan sonlarning valyutasi — yorliqlar shundan oladi. */
  currency = computed(() => this.overview()?.currency ?? this.currencySvc.defaultCode());

  /**
   * `Purchase` ("Xarid") ataylab YO'Q. Tovar xaridi xarajat emas — u sotilganda
   * tannarx bo'lib foydadan ayriladi, shuning uchun bu yerga yozilsa summa ikki
   * marta ayrilardi. Mol kirimi "Kirim" bo'limidan kiritiladi.
   * Backend ham bu kategoriyani rad etadi (`CreateExpenseCommand`).
   */
  categories: { label: string; value: ExpenseCategory }[] = [
    { label: 'EXPENSE_CATEGORY.Rent', value: 'Rent' },
    { label: 'EXPENSE_CATEGORY.Salary', value: 'Salary' },
    { label: 'EXPENSE_CATEGORY.Utilities', value: 'Utilities' },
    { label: 'EXPENSE_CATEGORY.Transport', value: 'Transport' },
    { label: 'EXPENSE_CATEGORY.Other', value: 'Other' },
  ];
  paymentMethods: { label: string; value: PaymentMethod }[] = [
    { label: 'FINANCE.METHOD.Cash', value: 'Cash' },
    { label: 'FINANCE.METHOD.Card', value: 'Card' },
    { label: 'FINANCE.METHOD.Bank', value: 'Bank' },
  ];

  // Recurring dialog
  recVisible = false;
  recSaving = false;
  recId: string | null = null;
  recForm: {
    storeId: string | null;
    category: ExpenseCategory;
    amount: number;
    dayOfMonth: number;
    description: string;
    paymentMethod: PaymentMethod;
    isActive: boolean;
  } = {
    storeId: null,
    category: 'Rent',
    amount: 0,
    dayOfMonth: 1,
    description: '',
    paymentMethod: 'Cash',
    isActive: true,
  };

  ngOnInit() {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    this.range = [monthStart, today];
    this.storeSvc
      .getAllLite()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => this.stores.set(res.data ?? []));
    this.loadOverview();
    this.loadRecurring();
    this.loadAging();
  }

  // ── Qarzlar yosh tahlili ────────────────────────────────────────
  aging = signal<DebtAgingDto | null>(null);

  /**
   * Alohida so'rov — umumiy `loading` bilan tugamaydi. Bitta bayroq ishlatilsa
   * bo'lim asosiy ma'lumot kelgach ham skeletda qolib ketardi.
   */
  agingLoading = signal(false);

  /** Guruhlar ekranda shu tartibda — eng yangisidan eng eskisiga. */
  readonly agingBuckets: DebtAgingBucket[] = [
    'NotDue',
    'Days0_30',
    'Days31_60',
    'Days61_90',
    'Over90',
  ];

  loadAging() {
    this.agingLoading.set(true);
    this.svc
      .getDebtAging(this.filterStoreId ?? undefined, this.selectedCurrency() || undefined)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.agingLoading.set(false)),
      )
      .subscribe((res) => this.aging.set(res.data ?? null));
  }

  bucketAmount(rows: { bucket: DebtAgingBucket; amount: number }[], b: DebtAgingBucket): number {
    return rows.find((r) => r.bucket === b)?.amount ?? 0;
  }

  /** Kechikkan qarz qizil bo'ladi — muddati kelmagani esa oddiy. */
  isOverdueBucket(b: DebtAgingBucket): boolean {
    return b !== 'NotDue';
  }

  loadOverview() {
    const from = this.range?.[0];
    const to = this.range?.[1];
    if (!from || !to) return;
    const end = new Date(to);
    end.setHours(23, 59, 59, 999);
    this.loading.set(true);
    this.svc
      .getOverview(
        from.toISOString(),
        end.toISOString(),
        this.filterStoreId ?? undefined,
        this.selectedCurrency() || undefined,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.overview.set(res.data);
          // Birinchi yuklashda backend kotirovka valyutasini tanlaydi — tanlagich
          // shu bilan moslashadi, aks holda bo'sh turib qolardi.
          if (res.data?.currency) this.selectedCurrency.set(res.data.currency);
        },
        complete: () => this.loading.set(false),
      });
  }

  loadRecurring() {
    this.svc
      .getRecurring()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => this.recurring.set(res.data ?? []));
  }

  onFilterChange() {
    this.loadOverview();
    this.loadAging();
  }

  /** Valyuta almashganda darhol qayta yuklanadi — "Qo'llash" bosish shart emas. */
  onCurrencyChange(code: string) {
    this.selectedCurrency.set(code);
    this.loadOverview();
    this.loadAging();
  }

  // ── Recurring CRUD ─────────────────────────────────────────────
  openCreateRec() {
    this.recId = null;
    this.recForm = {
      storeId: this.stores()[0]?.id ?? null,
      category: 'Rent',
      amount: 0,
      dayOfMonth: 1,
      description: '',
      paymentMethod: 'Cash',
      isActive: true,
    };
    this.recVisible = true;
  }

  openEditRec(r: RecurringExpenseDto) {
    this.recId = r.id;
    this.recForm = {
      storeId: r.storeId,
      category: r.category,
      amount: r.amount,
      dayOfMonth: r.dayOfMonth,
      description: r.description,
      paymentMethod: r.paymentMethod,
      isActive: r.isActive,
    };
    this.recVisible = true;
  }

  saveRec() {
    // Tanlagich yashirilgan bo'lsa qiymat `openCreateRec()` da qo'yiladi; do'kon
    // ro'yxati kechikib kelgan holat uchun zaxira.
    this.recForm.storeId ??= this.storePick.single()?.id ?? null;

    if (!this.recForm.storeId) {
      this.svc.showWarn(this.translate.instant('FINANCE_PAGE.VALIDATION.STORE'));
      return;
    }
    if (this.recForm.amount <= 0) {
      this.svc.showWarn(this.translate.instant('FINANCE_PAGE.VALIDATION.AMOUNT'));
      return;
    }
    if (this.recForm.dayOfMonth < 1 || this.recForm.dayOfMonth > 28) {
      this.svc.showWarn(this.translate.instant('FINANCE_PAGE.VALIDATION.DAY'));
      return;
    }
    this.recSaving = true;
    const done = {
      next: () => {
        this.svc.showSuccess();
        this.recVisible = false;
        this.loadRecurring();
      },
      error: () => (this.recSaving = false),
      complete: () => (this.recSaving = false),
    };
    if (this.recId) {
      this.svc
        .updateRecurring(this.recId, {
          category: this.recForm.category,
          amount: this.recForm.amount,
          dayOfMonth: this.recForm.dayOfMonth,
          description: this.recForm.description,
          paymentMethod: this.recForm.paymentMethod,
          isActive: this.recForm.isActive,
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(done);
    } else {
      this.svc
        .createRecurring({
          storeId: this.recForm.storeId,
          category: this.recForm.category,
          amount: this.recForm.amount,
          dayOfMonth: this.recForm.dayOfMonth,
          description: this.recForm.description,
          paymentMethod: this.recForm.paymentMethod,
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(done);
    }
  }

  deleteRec(r: RecurringExpenseDto) {
    this.confirm.confirm({
      message: this.translate.instant('FINANCE_PAGE.REC.DELETE_CONFIRM'),
      header: this.translate.instant('COMMON.CONFIRM'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.svc
          .deleteRecurring(r.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.svc.showSuccess();
              this.loadRecurring();
            },
          });
      },
    });
  }
}
